import "./style.scss";

import { Avatar, Box, CircularProgress, Tooltip } from "@mui/material";
import ActionMenuMore from "assets/img/svg/actionMenuMore";
import Import from "assets/img/svg/import";
import Plus from "assets/img/svg/plus";
import Upload from "assets/img/svg/upload";
import ImportData from "components/ImportData";
import ModalBox from "components/Modal";
import FilterInput from "components/shared/FilterInput";
import StyledButton from "components/shared/StyledButton";
import TagsFilter from "components/shared/TagsFilter";
import type { ITitle } from "containers/Titles/types";
import format from "date-fns/format";
import { getCleanStartorEndDay } from "helpers/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TParamsPagination } from "store/types";

import variables from "../../assets/styles/_variables.module.scss";
import { exportTitleList, fetchTags, fetchTitles } from "./../../store/titleStore";

type TProps = {
    currentTitle: number;
    history: any;
    setCurrentTitle: (title: ITitle) => void;
    titles: ITitle[];
};

export const whiteButton = {
    "&.MuiButton-root": {
        color: "black",
        backgroundColor: "white",
        border: `1px solid ${variables["border-grey"]}`,
        borderRadius: variables["border-radius"],
        padding: 0,
    },
};

// this is not used anymore, it got replaced by ElementList component
const TitlesList = (props: TProps) => {
    const navigate = useNavigate();
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [chosenTags, setChosenTags] = useState<string[]>([]);
    const [showPopover, setShowPopover] = useState(false);
    const nodeRef = useRef<HTMLDivElement | null>(null);
    const [showImportTitleModal, setShowImportTitleModal] = useState(false);
    const [showImportTermsModal, setShowImportTermsModal] = useState(false);
    const { titles, titlesStatus, countTitles } = useAppSelector((state) => state.titles);
    const tagList = useAppSelector((state) => state.titles.tags);

    const [positionScroll, setPositionScroll] = useState<number>(0);
    const [query, setQuery] = useState<string>("");
    const [timeOut, setTimeOut] = useState<NodeJS.Timeout | undefined>();
    const [queryIsTyping, setQueryIsTyping] = useState<boolean | null>(null);
    const isMounted = useRef(false);

    const handleQuery = (e: any) => {
        setQuery(e);
        // Debounce call endpoint when search
        if (!queryIsTyping) {
            setTimeOut(
                setTimeout(() => {
                    setQueryIsTyping(false);
                }, 700),
            );
            setQueryIsTyping(true);
        } else {
            clearTimeout(timeOut);
            setTimeOut(
                setTimeout(() => {
                    setQueryIsTyping(false);
                }, 700),
            );
        }
    };

    const handleExport = () => dispatch(exportTitleList());

    const handleClosePopover = useCallback(() => {
        setShowPopover(false);
    }, []);

    const handleScroll = (ev: any) => {
        const target = ev.target as HTMLElement;
        setPositionScroll(
            (target.offsetHeight * 100) / target.scrollHeight + (target.scrollTop * 100) / target.scrollHeight,
        );
    };

    const handleRequest = (offset?: number) => {
        const options: TParamsPagination = { limit: 100 };
        offset && (options.offset = offset);
        query && (options.query = query);
        chosenTags.length && (options.tags = chosenTags);
        dispatch(fetchTitles(options));
    };

    useEffect(() => {
        if (queryIsTyping === false) {
            handleRequest();
        }
    }, [queryIsTyping]);

    useEffect(() => {
        if (titlesStatus !== "pending" && positionScroll > 70 && titles.length < countTitles) {
            handleRequest(titles.length);
        }
    }, [positionScroll]);

    useEffect(() => {
        if (isMounted.current) {
            handleRequest();
        }
    }, [chosenTags]);

    useEffect(() => {
        window.addEventListener("click", handleClosePopover);
        return () => {
            window.removeEventListener("click", handleClosePopover);
        };
    });

    useEffect(() => {
        if (tagList == null) dispatch(fetchTags());
    }, [tagList]);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
    }, []);

    return (
        <div style={{ minWidth: 335 }}>
            <div className="titles-list-container">
                <div className="titles-list-head">
                    <div className="titles-list-header" onClick={(e) => e.stopPropagation()}>
                        {`${t("Titles.Titles")}
            (${countTitles})`}
                        <StyledButton
                            onClick={() => setShowPopover((prev) => !prev)}
                            sx={{ ...whiteButton, minHeight: "120%" }}
                        >
                            <div className="titles-list-menu-button">
                                <ActionMenuMore />
                            </div>
                        </StyledButton>
                    </div>
                    <div className="titles-list-actions">
                        <div className="titles-list-actions-left">
                            <StyledButton fullWidth buttonType="primary" onClick={() => navigate("/new-title")}>
                                <Plus />
                                {t("Titles.Title")}
                            </StyledButton>
                        </div>
                        <CSSTransition
                            nodeRef={nodeRef}
                            in={showPopover}
                            timeout={200}
                            classNames="tags-selector"
                            unmountOnExit
                        >
                            <div className="titles-list-actions-popover" ref={nodeRef}>
                                <div className="titles-list-actions-title">Imports</div>
                                <StyledButton
                                    onClick={() => setShowImportTitleModal(true)}
                                    sx={{
                                        ...whiteButton,
                                        display: "flex",
                                        gap: "8px",
                                        minHeight: "100%",
                                    }}
                                >
                                    <Upload />
                                    {t("ImportData.ImportTitle")}
                                </StyledButton>
                                <StyledButton
                                    onClick={() => setShowImportTermsModal(true)}
                                    sx={{
                                        ...whiteButton,
                                        display: "flex",
                                        gap: "8px",
                                        minHeight: "100%",
                                    }}
                                >
                                    <Upload />
                                    {t("ImportData.ImportTerms")}
                                </StyledButton>
                                <div className="titles-list-actions-title">Exports</div>
                                <StyledButton
                                    onClick={handleExport}
                                    sx={{
                                        ...whiteButton,
                                        display: "flex",
                                        gap: "8px",
                                        minHeight: "100%",
                                    }}
                                >
                                    <Import />
                                    {t("ImportData.ExportTitle")}
                                </StyledButton>
                            </div>
                        </CSSTransition>
                    </div>
                    <div className="titles-list-filter">
                        <div className="titles-list-search">
                            <FilterInput filterValue={query} setFilterValue={handleQuery} />
                        </div>
                        <TagsFilter uniqTags={tagList} chosenTags={chosenTags} setChosenTags={setChosenTags} />
                    </div>
                </div>
                <div className="titles-list" onScroll={(ev) => handleScroll(ev)}>
                    {!titles.length && titlesStatus === "pending" ? (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress sx={{ color: variables["primary-purple"] }} />
                        </Box>
                    ) : (
                        [...titles].reverse().map((title, index) => (
                            <div
                                key={`${title.id}_${index}`}
                                className={`titles-list-element${title.id === props.currentTitle ? " active" : ""}`}
                                onClick={() => props.setCurrentTitle(title)}
                            >
                                <div
                                    className="avatar-block"
                                    style={{
                                        display: "flex",
                                        alignItems: "stretch",
                                        gap: "10px",
                                    }}
                                >
                                    <Avatar
                                        variant="rounded"
                                        src={title.avatarUrl}
                                        imgProps={{ sx: { objectFit: "cover" } }}
                                        sx={{
                                            backgroundColor: "#FFF2F1",
                                            // backgroundColor: title.background,
                                            color: variables["foreground"],
                                            fontSize: "14px",
                                        }}
                                    >
                                        {title.name.slice(0, 1)}
                                    </Avatar>
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <div className="titles-list-element-tags">
                                            {title.tags && (
                                                <>
                                                    {title.tags
                                                        .slice(0)
                                                        .reverse()
                                                        .map((item, i) => {
                                                            return i < 15 ? (
                                                                <Tooltip
                                                                    key={`tooltip-${item.id}`}
                                                                    title={item.name}
                                                                    arrow
                                                                >
                                                                    <span
                                                                        className={`titles-list-element-tag-${item.color}`}
                                                                        key={item.id}
                                                                    ></span>
                                                                </Tooltip>
                                                            ) : null;
                                                        })}
                                                </>
                                            )}
                                            <span className={`titles-list-element-tag`}></span>
                                        </div>
                                        <span title={title.name} className="title-list-title-name">
                                            {title.name}
                                        </span>
                                        <span style={{ fontSize: "12px", color: "#575757" }}>{title.ean13}</span>
                                    </div>
                                </div>
                                <div className="date-block">
                                    {title.releaseDate ? format(getCleanStartorEndDay(title.releaseDate), "d MMM") : ""}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
            <ModalBox
                open={showImportTitleModal}
                dictionaryKey="titles"
                onClose={() => setShowImportTitleModal(false)}
                mainTitle={t("ImportData.ImportTitle")}
            >
                <ImportData dictionaryKey="titles" close={() => setShowImportTitleModal(false)} />
            </ModalBox>
            <ModalBox
                open={showImportTermsModal}
                dictionaryKey="terms"
                onClose={() => setShowImportTermsModal(false)}
                mainTitle={t("ImportData.ImportTerms")}
            >
                <ImportData dictionaryKey="terms" close={() => setShowImportTermsModal(false)} />
            </ModalBox>
        </div>
    );
};

export default TitlesList;
